import { Injectable, Injector } from '@angular/core';
import { PlatformLocation, registerLocaleData } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import * as moment from 'moment-timezone';
import { filter as _filter, merge as _merge } from 'lodash-es';
import { AppConsts, LocaleMapping } from '@shared/AppConsts';
import { AppSessionService } from '@shared/session/app-session.service';
import { environment } from '@environments/environment';

/**
 * Neither endpoint has a generated proxy: AbpUserConfiguration is served by ABP itself and
 * the app config is a static JSON asset. These describe only the fields actually read —
 * the ABP payload is otherwise merged wholesale into the global `abp` object.
 */
interface AbpUserConfigurationResponse {
    result: Record<string, unknown> & {
        clock: { provider: string };
    };
}

interface AppConfigResponse {
    remoteServiceBaseUrl: string;
    localeMappings: LocaleMapping[];
}

@Injectable({ providedIn: 'root' })
export class AppInitializer {
    constructor(
        private _injector: Injector,
        private _platformLocation: PlatformLocation,
        private _httpClient: HttpClient,
    ) {}

    init(): () => Promise<boolean> {
        return () => {
            return new Promise<boolean>((resolve, reject) => {
                AppConsts.appBaseHref = this._getBaseHref();
                const appBaseUrl = this._getDocumentOrigin() + AppConsts.appBaseHref;
                const fail = (err: unknown): void => this._failBootstrap(reject, err);
                this._getApplicationConfig(
                    appBaseUrl,
                    () => {
                        this._getUserConfiguration(() => {
                            abp.event.trigger('abp.dynamicScriptsInitialized');
                            // do not use constructor injection for AppSessionService
                            const appSessionService = this._injector.get(AppSessionService);
                            appSessionService.init().then((result) => {
                                if (this._shouldLoadLocale()) {
                                    const angularLocale = this._convertAbpLocaleToAngularLocale(abp.localization.currentLanguage.name);
                                    import(
                                        /* webpackInclude: /(en|it)\.mjs/ */
                                        /* webpackMode: "lazy-once" */
                                        /* webpackChunkName: "i18n-base" */
                                        `/node_modules/@angular/common/locales/${angularLocale}`
                                    ).then((module) => {
                                        registerLocaleData(module.default);
                                        resolve(result);
                                    }, fail);
                                } else {
                                    resolve(result);
                                }
                            }, fail);
                        }, fail);
                    },
                    fail,
                );
            });
        };
    }

    /**
     * Bootstrap failed before Angular (and localization) existed, so the message ships pre-rendered
     * in index.html and is only revealed here — otherwise the splash promises progress that never comes.
     */
    private _failBootstrap(reject: (reason?: unknown) => void, err: unknown): void {
        document.querySelector('.app-splash')?.classList.add('app-splash--failed');
        reject(err);
    }

    private _getBaseHref(): string {
        const baseUrl = this._platformLocation.getBaseHrefFromDOM();
        if (baseUrl) {
            return baseUrl;
        }

        return '/';
    }

    private _getDocumentOrigin(): string {
        if (!document.location.origin) {
            const port = document.location.port ? ':' + document.location.port : '';
            return document.location.protocol + '//' + document.location.hostname + port;
        }

        return document.location.origin;
    }

    private _shouldLoadLocale(): boolean {
        return abp.localization.currentLanguage.name && abp.localization.currentLanguage.name !== 'en-US';
    }

    private _convertAbpLocaleToAngularLocale(locale: string): string {
        if (!AppConsts.localeMappings) {
            return locale;
        }

        const localeMapings = _filter(AppConsts.localeMappings, { from: locale });
        if (localeMapings && localeMapings.length) {
            return localeMapings[0]['to'];
        }

        return locale;
    }

    private _getCurrentClockProvider(currentProviderName: string): abp.timing.IClockProvider {
        if (currentProviderName === 'unspecifiedClockProvider') {
            return abp.timing.unspecifiedClockProvider;
        }

        if (currentProviderName === 'utcClockProvider') {
            return abp.timing.utcClockProvider;
        }

        return abp.timing.localClockProvider;
    }

    private _getUserConfiguration(callback: () => void, onError?: (err: unknown) => void): void {
        const cookieLangValue = abp.utils.getCookieValue('Abp.Localization.CultureName');
        const token = abp.auth.getToken();

        const requestHeaders = {
            'Abp.TenantId': `${abp.multiTenancy.getTenantIdCookie()}`,
            '.AspNetCore.Culture': `c=${cookieLangValue}|uic=${cookieLangValue}`,
        };

        if (token) {
            requestHeaders['Authorization'] = `Bearer ${token}`;
        }

        this._httpClient
            .get<AbpUserConfigurationResponse>(`${AppConsts.remoteServiceBaseUrl}/AbpUserConfiguration/GetAll`, {
                headers: requestHeaders,
            })
            .subscribe((response) => {
                const result = response.result;

                _merge(abp, result);

                abp.clock.provider = this._getCurrentClockProvider(result.clock.provider);

                moment.locale(abp.localization.currentLanguage.name);

                if (abp.clock.provider.supportsMultipleTimezone) {
                    moment.tz.setDefault(abp.timing.timeZoneInfo.iana.timeZoneId);
                }

                callback();
            }, onError);
    }

    private _getApplicationConfig(appRootUrl: string, callback: () => void, onError?: (err: unknown) => void) {
        this._httpClient
            .get<AppConfigResponse>(`${appRootUrl}assets/${environment.appConfig}`, {
                headers: {
                    'Abp.TenantId': `${abp.multiTenancy.getTenantIdCookie()}`,
                },
            })
            .subscribe((response) => {
                AppConsts.appBaseUrl = appRootUrl;
                AppConsts.remoteServiceBaseUrl = response.remoteServiceBaseUrl;
                AppConsts.localeMappings = response.localeMappings;

                callback();
            }, onError);
    }
}
